import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  ArrowRight, Shield, Truck, Award, ClipboardCheck, Star,
  ChevronDown, ChevronRight, Laptop, Monitor, Smartphone, Wrench,
  Repeat, Headphones, Search, Sparkles, Zap, BadgeCheck, Bell, Package,
  ShoppingCart, Lock, Clock, Heart, RefreshCw, Check, Gift, Percent,
  Tablet, Printer, Mouse, Keyboard, Cpu, HardDrive,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import HeroBanner from "@/components/HeroBanner";
import { Button } from "@/components/ui/button";
import ProductCard from "@/components/ProductCard";
import { useCategories } from "@/hooks/useCategories";
import { useProducts } from "@/hooks/useProducts";
import { useCMSContent } from "@/hooks/useCMSContent";
import { useState, useMemo, lazy, Suspense, useRef, startTransition } from "react";
import FadeIn from "@/components/FadeIn";
import PageTransition from "@/components/PageTransition";
import SEOManager from "@/components/SEOManager";
import { generateProductUrl } from "@/lib/utils";
import { Product } from "@/types/product";
import { memo } from "react";
import { cn } from "@/lib/utils";
import { useCart } from "@/context/CartContext";
import fixswiftLogo from "@/assets/fixswift-logo.svg";

const HappyCustomers = lazy(() => import("@/components/home/HappyCustomers"));
const LatestBlogs = lazy(() => import("@/components/home/LatestBlogs"));

/* ── Icon map for CMS-driven icons ── */
const ICON_MAP: Record<string, React.ComponentType<any>> = {
  shield: Shield, truck: Truck, award: Award, check: Check, star: Star,
  heart: Heart, lock: Lock, clock: Clock, zap: Zap, refresh: RefreshCw,
  gift: Gift, percent: Percent, laptop: Laptop, monitor: Monitor,
  smartphone: Smartphone, wrench: Wrench, repeat: Repeat, headphones: Headphones,
  tablet: Tablet, printer: Printer, mouse: Mouse, keyboard: Keyboard,
  cpu: Cpu, "hard-drive": HardDrive,
};

const getIcon = (name: string) => ICON_MAP[name] || Shield;

/* ── Fallback data ── */
const FALLBACK_CATEGORIES = [
  { icon: "laptop", label: "Laptops", link: "/products?category=laptop", color: "from-blue-500 to-blue-600" },
  { icon: "monitor", label: "Desktops", link: "/products?category=desktop", color: "from-violet-500 to-violet-600" },
  { icon: "smartphone", label: "Mobiles", link: "/products?category=mobile-phone", color: "from-emerald-500 to-emerald-600" },
  { icon: "wrench", label: "Repair", link: "/repair", color: "from-amber-500 to-amber-600" },
  { icon: "repeat", label: "Sell Device", link: "/sell", color: "from-pink-500 to-pink-600" },
  { icon: "headphones", label: "Accessories", link: "/products?category=accessories", color: "from-cyan-500 to-cyan-600" },
];

const FALLBACK_TRUST = [
  { icon: "shield", label: "6 Months Warranty", description: "On all devices" },
  { icon: "check", label: "Tested Products", description: "Quality assured" },
  { icon: "award", label: "GST Invoice", description: "100% compliant" },
  { icon: "truck", label: "Home Delivery", description: "Across India" },
];

const FALLBACK_WHY_CHOOSE = {
  heading: "Why Choose FixSwift",
  subtitle: "India's most trusted destination for certified refurbished tech.",
  items: [
    { icon: "shield", label: "6 Months Warranty", description: "Peace of mind with a comprehensive 6-month warranty on all certified refurbished devices.", link: "/warranty-policy" },
    { icon: "truck", label: "Home Delivery", description: "Fast, safe, and reliable doorstep delivery across India. Free shipping on orders over ₹15,000.", link: "" },
    { icon: "award", label: "GST Billing", description: "100% compliant GST invoicing for business users to claim input tax credit effortlessly.", link: "" },
  ],
};

const FALLBACK_CTA = [
  { badge_emoji: "💰", badge_text: "Sell to us", heading: "Sell Used Devices", description: "Free pickup. Instant payment. Best prices.", cta_text: "Check Value", link: "/sell", style: "filled" },
  { badge_emoji: "🏢", badge_text: "B2B & Enterprise", heading: "Buy in Bulk", description: "Wholesale pricing with GST input credit.", cta_text: "Request Quote", link: "/bulk-order", style: "outlined" },
];

const FALLBACK_FAQ = [
  { q: "What does 'Certified Refurbished' mean?", a: "Every device undergoes a 21-point quality check. Components are tested, cleaned, and restored to near-new condition." },
  { q: "Do products come with a warranty?", a: "Yes — minimum 6-month warranty covering hardware defects, with extended options available." },
  { q: "Can I get a GST invoice?", a: "Absolutely. 100% GST-compliant invoices for every order." },
  { q: "What is the return policy?", a: "7-day testing period. Not satisfied? Request a return or replacement." },
];

const FAQItem = ({ q, a }: { q: string; a: string }) => {
  const [open, setOpen] = useState(false);
  return (
    <div onClick={() => setOpen(!open)} className="border-b border-border/50 py-3 cursor-pointer group">
      <div className="flex items-start justify-between gap-3">
        <span className="font-heading text-[13px] font-bold text-foreground group-hover:text-primary transition-colors">{q}</span>
        <ChevronDown
          className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-300"
          style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)" }}
        />
      </div>
      <div className="overflow-hidden transition-all duration-300" style={{ maxHeight: open ? "200px" : "0", opacity: open ? 1 : 0 }}>
        <p className="pt-2 text-[12px] leading-relaxed text-muted-foreground">{a}</p>
      </div>
    </div>
  );
};

/* ═══ MOBILE PRODUCT CARD — Compact Flipkart/Amazon Style ═══ */
const MobileProductCard = memo(({ product }: { product: Product }) => {
  const discount = product.originalPrice > product.price
    ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)
    : 0;

  return (
    <Link to={generateProductUrl(product.id, product.name)} className="group block w-full">
      <div className="flex flex-col bg-card rounded-xl overflow-hidden active:scale-[0.98] transition-transform border border-border/20">
        {/* Image — contained in box */}
        <div className="relative aspect-[1/1] bg-muted/10 overflow-hidden">
          {discount > 0 && (
            <span className="absolute top-1.5 left-1.5 bg-primary text-primary-foreground text-[9px] font-black px-1.5 py-[3px] rounded z-10 leading-none">
              {discount}% off
            </span>
          )}
          <img
            src={product.images[0]}
            alt={product.name}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>

        {/* Info */}
        <div className="px-2.5 pt-2.5 pb-3 flex flex-col flex-1">
          <h3 className="text-[11.5px] font-bold text-foreground leading-[1.35] line-clamp-2 tracking-[-0.01em]">
            {product.name}
          </h3>

          {/* Spec chips */}
          {(product.ram || product.storage) && (
            <div className="flex flex-wrap gap-1 mt-1.5">
              {product.ram && (
                <span className="text-[9px] font-semibold text-foreground/60 bg-muted/40 px-2 py-[3px] rounded-md border border-border/20 leading-none">
                  {product.ram}
                </span>
              )}
              {product.storage && (
                <span className="text-[9px] font-semibold text-foreground/60 bg-muted/40 px-2 py-[3px] rounded-md border border-border/20 leading-none">
                  {product.storage}
                </span>
              )}
            </div>
          )}

          {/* Price block */}
          <div className="mt-auto pt-2">
            <div className="flex items-baseline gap-1">
              <span className="text-[16px] font-black text-foreground leading-none tracking-tight">
                ₹{product.price.toLocaleString("en-IN")}
              </span>
              {discount > 0 && (
                <span className="text-[10px] text-muted-foreground/70 line-through leading-none">
                  ₹{product.originalPrice.toLocaleString("en-IN")}
                </span>
              )}
            </div>

            {/* Warranty row */}
            <div className="flex items-center gap-1 mt-1.5 opacity-80">
              <Shield className="h-3 w-3 text-primary" />
              <span className="text-[9px] font-bold text-primary">{product.warranty}</span>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
});
MobileProductCard.displayName = "MobileProductCard";

/* ═══ PRODUCT SKELETON CARD ═══ */
const ProductSkeleton = () => (
  <div className="flex flex-col bg-card rounded-xl border border-border/30 overflow-hidden animate-pulse">
    <div className="aspect-square bg-muted/40" />
    <div className="p-2.5 space-y-2">
      <div className="h-2.5 w-full bg-muted/60 rounded" />
      <div className="h-2.5 w-3/4 bg-muted/60 rounded" />
      <div className="flex items-center gap-1 pt-1">
        <div className="h-3.5 w-16 bg-muted/60 rounded" />
      </div>
    </div>
  </div>
);

const ProductSkeletonRow = ({ count = 4 }: { count?: number }) => (
  <>
    <div className="md:hidden flex overflow-x-auto gap-2.5 scrollbar-hide pb-2 -mx-3 px-3">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="shrink-0 w-[150px]"><ProductSkeleton /></div>
      ))}
    </div>
    <div className="hidden md:grid gap-4 grid-cols-2 lg:grid-cols-4">
      {Array.from({ length: count }).map((_, i) => (
        <ProductSkeleton key={i} />
      ))}
    </div>
  </>
);

const Index = () => {
  const { data: products = [], isLoading: productsLoading } = useProducts();
  const { data: categories = [] } = useCategories();
  const { data: cmsSections = [] } = useCMSContent();
  const { user, profile } = useAuth();
  const { totalItems } = useCart();
  const featured = products.filter((p) => p.featured);
  const allProducts = products.slice(0, 8);

  const getContent = (key: string) => cmsSections.find((s) => s.section_key === key)?.content;

  // CMS-driven data with fallbacks
  const faqContent = getContent("faq");
  const faqs: { q: string; a: string }[] = faqContent?.items?.length ? faqContent.items : FALLBACK_FAQ;

  const bannersContent = getContent("banners");
  const banners: { title: string; subtitle: string; link: string; bg_color: string; is_active: boolean }[] =
    bannersContent?.items?.filter((b: { is_active?: boolean }) => b.is_active) ?? [];

  const categoriesContent = getContent("categories");
  const categoryItems = categoriesContent?.items?.length ? categoriesContent.items : FALLBACK_CATEGORIES;

  const trustContent = getContent("trust_badges");
  const rawTrustBadges = trustContent?.badges?.length ? trustContent.badges : FALLBACK_TRUST;
  // Normalize: CMS may store badges as plain strings or as objects
  const trustBadges = rawTrustBadges.map((b: any) =>
    typeof b === "string" ? { icon: "shield", label: b, description: "" } : b
  );

  const whyContent = getContent("why_choose");
  const whyChoose = whyContent?.items?.length ? whyContent : FALLBACK_WHY_CHOOSE;

  const ctaContent = getContent("cta_sections");
  const ctaItems = ctaContent?.items?.length ? ctaContent.items : FALLBACK_CTA;

  const faqSchema = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
  }), [faqs]);

  const orgSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "FixSwift Computers",
    url: "https://fixswift.in",
    description: "India's trusted destination for certified refurbished laptops and desktops with warranty and GST billing.",
  };

  const greeting = new Date().getHours() < 12 ? "Good Morning" : new Date().getHours() < 17 ? "Good Afternoon" : "Good Evening";
  const userName = profile?.full_name || user?.user_metadata?.full_name || user?.email?.split("@")[0] || "User";

  return (
    <PageTransition>
      <SEOManager
        title="Refurbished Laptops at Best Price | FixSwift India"
        description="Buy top-quality refurbished laptops and desktops in India. Get 6 months warranty, GST billing, and safe home delivery. Save up to 60%."
        url="https://fixswift.in/"
        image="https://fixswift.in/og-image.png"
        schemaOrg={[faqSchema, orgSchema]}
      />

      <div className="flex flex-col bg-background pb-16 md:pb-0">
        <h1 className="sr-only">Refurbished Laptops at Best Price in India</h1>

        {/* ═══ MOBILE TOP BAR — Enhanced ═══ */}
        <div
          className="md:hidden bg-primary sticky z-40"
          style={{ top: "calc(56px + env(safe-area-inset-top, 0px))" }}
        >
          {/* Greeting Row — no duplicate icons, navbar handles cart/profile */}
          <div className="flex items-center px-3.5 pt-3 pb-2">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              {user ? (
                <>
                  <Link to="/account" className="shrink-0 active:scale-90 transition-transform">
                    {profile?.profile_image ? (
                      <img src={profile.profile_image} alt={userName} className="h-9 w-9 rounded-full object-cover ring-2 ring-primary-foreground/25 shadow-md" width={36} height={36} />
                    ) : (
                      <div className="h-9 w-9 rounded-full bg-primary-foreground/20 ring-2 ring-primary-foreground/25 flex items-center justify-center shadow-md">
                        <span className="text-[13px] font-black text-primary-foreground leading-none">{userName.charAt(0).toUpperCase()}</span>
                      </div>
                    )}
                  </Link>
                  <div className="min-w-0">
                    <p className="text-[11px] text-primary-foreground/75 font-medium leading-none">{greeting} 👋</p>
                    <p className="text-[14px] font-bold text-primary-foreground leading-tight mt-0.5 truncate">{userName}</p>
                  </div>
                </>
              ) : (
                <>
                  <img src={fixswiftLogo} alt="FixSwift" className="h-6 w-6 brightness-0 invert" width={24} height={24} />
                  <span className="text-[15px] font-black text-primary-foreground tracking-tight">FixSwift</span>
                </>
              )}
            </div>
          </div>

          {/* Search Bar */}
          <div className="px-3.5 pb-3">
            <Link to="/products" className="flex items-center gap-2.5 w-full h-[42px] rounded-xl bg-primary-foreground px-3.5 active:scale-[0.98] transition-transform shadow-sm">
              <Search className="h-4 w-4 shrink-0 text-muted-foreground/70" />
              <span className="text-[13px] text-muted-foreground font-medium">Search laptops, desktops...</span>
            </Link>
          </div>

          {/* Category tabs */}
          <div className="bg-primary-foreground border-t border-border/10">
            <div className="flex overflow-x-auto scrollbar-hide">
              {categoryItems.map((cat: any) => {
                const Icon = getIcon(cat.icon);
                return (
                  <Link
                    key={cat.label}
                    to={cat.link}
                    className="flex flex-col items-center gap-1 px-4 py-2.5 shrink-0 active:bg-muted/30 transition-colors min-w-[72px]"
                  >
                    <div className="h-8 w-8 rounded-full bg-muted/40 flex items-center justify-center">
                      <Icon className="h-4 w-4 text-foreground/70" strokeWidth={1.8} />
                    </div>
                    <span className="text-[10px] font-semibold text-foreground/80 whitespace-nowrap">{cat.label}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>

        {/* ═══ PROMO STRIP ═══ */}
        {banners.length > 0 ? (
          banners.map((banner, i) => (
            <div
              key={i}
              className="relative overflow-hidden text-center py-2 text-[11px] font-semibold text-primary-foreground tracking-wide"
              style={{ backgroundColor: banner.bg_color }}
            >
              {banner.link ? (
                <Link to={banner.link} className="relative z-10 flex items-center justify-center gap-2 px-2">
                  <Sparkles className="w-3 h-3 text-amber-300 animate-pulse" />
                  <span className="font-black uppercase tracking-wider">{banner.title}</span>
                  <span className="w-1 h-1 rounded-full bg-primary-foreground/40" />
                  <span className="font-medium">{banner.subtitle}</span>
                </Link>
              ) : (
                <span className="relative z-10 flex items-center justify-center gap-2 px-2">
                  <Sparkles className="w-3 h-3 text-amber-300 animate-pulse" />
                  <span className="font-black uppercase tracking-wider">{banner.title}</span>
                  <span className="w-1 h-1 rounded-full bg-primary-foreground/40" />
                  <span className="font-medium">{banner.subtitle}</span>
                </span>
              )}
            </div>
          ))
        ) : (
          <div className="relative overflow-hidden bg-secondary text-center py-2 text-[11px] font-semibold text-secondary-foreground tracking-wide">
            <span className="flex items-center justify-center gap-2 px-2">
              <Truck className="w-3 h-3" />
              <span className="font-black uppercase tracking-wider">Free Shipping</span>
              <span className="w-1 h-1 rounded-full bg-secondary-foreground/40" />
              <span className="font-medium">On Orders Above ₹15,000</span>
            </span>
          </div>
        )}

        {/* ═══ HERO BANNER ═══ */}
        <HeroBanner />

        {/* ═══ TRUST STRIP — CMS driven ═══ */}
        <section className="md:hidden bg-card py-3 border-b border-border/20">
          <div className="flex overflow-x-auto scrollbar-hide gap-4 px-3">
            {trustBadges.map((badge: any, i: number) => {
              const Icon = getIcon(badge.icon);
              return (
                <div key={i} className="flex items-center gap-1.5 shrink-0">
                  <div className="h-7 w-7 rounded-full bg-primary/8 flex items-center justify-center shrink-0">
                    <Icon className="h-3.5 w-3.5 text-primary" strokeWidth={2.2} />
                  </div>
                  <div>
                    <p className="text-[10px] font-bold text-foreground whitespace-nowrap leading-none">{badge.label}</p>
                    <p className="text-[8px] text-muted-foreground whitespace-nowrap leading-tight">{badge.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* ═══ TRUST MARQUEE — Desktop ═══ */}
        <section className="hidden md:block bg-background py-2 border-b border-border/10 overflow-hidden">
          <div className="relative">
            <div className="flex animate-marquee gap-6 w-max">
              {[...trustBadges, ...trustBadges, ...trustBadges].map((badge: any, i: number) => {
                const Icon = getIcon(badge.icon);
                return (
                  <div key={i} className="flex items-center gap-1.5 shrink-0">
                    <Icon className="h-3.5 w-3.5 text-primary shrink-0" strokeWidth={2} />
                    <span className="text-sm font-bold text-foreground whitespace-nowrap">{badge.label}</span>
                    <span className="text-muted-foreground/30 ml-3">•</span>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ═══ CATEGORY GRID (Desktop) — CMS driven ═══ */}
        <section className="hidden md:block py-5 bg-background">
          <div className="container max-w-7xl px-4">
            <div className="flex justify-center gap-8">
              {categoryItems.map((cat: any) => {
                const Icon = getIcon(cat.icon);
                return (
                  <Link
                    key={cat.label}
                    to={cat.link}
                    className="group flex flex-col items-center gap-1.5 hover:scale-105 transition-transform"
                  >
                    <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${cat.color} flex items-center justify-center shadow-md`}>
                      <Icon className="h-6 w-6 text-white" strokeWidth={1.8} />
                    </div>
                    <span className="text-[13px] font-bold text-foreground text-center leading-tight">{cat.label}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>

        {/* ═══ WHY CHOOSE — CMS driven ═══ */}
        <section className="py-4 md:py-12 bg-muted/20">
          <div className="container max-w-7xl px-3 md:px-4">
            <FadeIn>
              <div className="text-center mb-3 md:mb-8">
                <h2 className="font-heading text-[15px] md:text-3xl font-black text-foreground mb-0.5">{whyChoose.heading}</h2>
                <p className="text-[11px] md:text-base text-muted-foreground">{whyChoose.subtitle}</p>
              </div>
            </FadeIn>

            {/* Mobile */}
            <div className="grid grid-cols-3 gap-2.5 md:hidden">
              {whyChoose.items.map((item: any, i: number) => {
                const Icon = getIcon(item.icon);
                const gradients = [
                  "from-primary/15 to-primary/5",
                  "from-emerald-500/15 to-emerald-500/5",
                  "from-amber-500/15 to-amber-500/5",
                ];
                const iconColors = ["text-primary", "text-emerald-600", "text-amber-600"];
                const dotColors = ["bg-primary", "bg-emerald-500", "bg-amber-500"];
                const Inner = (
                  <div className="relative flex flex-col items-center text-center bg-card py-5 px-2 rounded-2xl border border-border/30 shadow-sm active:scale-[0.96] transition-all overflow-hidden">
                    {/* Subtle gradient glow */}
                    <div className={`absolute inset-0 bg-gradient-to-b ${gradients[i % 3]} opacity-60 pointer-events-none`} />
                    <div className="relative">
                      <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${gradients[i % 3]} flex items-center justify-center mb-2.5 shadow-sm`}>
                        <Icon className={`w-5.5 h-5.5 ${iconColors[i % 3]}`} strokeWidth={1.8} />
                      </div>
                    </div>
                    <h3 className="relative text-[11px] font-bold text-foreground leading-snug whitespace-pre-line">{item.label.replace(" ", "\n")}</h3>
                    <div className={`relative w-4 h-0.5 ${dotColors[i % 3]} rounded-full mt-2 opacity-50`} />
                  </div>
                );
                return item.link ? <Link key={i} to={item.link}>{Inner}</Link> : <div key={i}>{Inner}</div>;
              })}
            </div>

            {/* Desktop */}
            <div className="hidden md:grid md:grid-cols-3 gap-6">
              {whyChoose.items.map((item: any, i: number) => {
                const Icon = getIcon(item.icon);
                const Inner = (
                  <div className="group flex flex-col items-center text-center bg-card p-8 rounded-2xl border border-border/40 shadow-sm hover:shadow-md transition-all hover:border-primary/20 hover:-translate-y-1">
                    <div className="w-16 h-16 bg-primary/10 text-primary rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform group-hover:bg-primary group-hover:text-primary-foreground">
                      <Icon className="w-8 h-8" />
                    </div>
                    <h3 className="font-heading text-xl font-bold mb-2 text-foreground">{item.label}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{item.description}</p>
                  </div>
                );
                return item.link ? (
                  <FadeIn key={i} delay={i * 0.1}><Link to={item.link}>{Inner}</Link></FadeIn>
                ) : (
                  <FadeIn key={i} delay={i * 0.1}>{Inner}</FadeIn>
                );
              })}
            </div>
          </div>
        </section>

        {/* ═══ FEATURED DEALS ═══ */}
        <section className="py-4 md:py-10 bg-background">
          <div className="container max-w-7xl px-3 md:px-4">
            <FadeIn>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="h-5 w-1 rounded-full bg-primary md:h-6 md:w-1.5" />
                  <div>
                    <h2 className="font-heading text-[15px] md:text-2xl font-black text-foreground leading-tight">Featured Deals</h2>
                    <p className="text-[10px] text-muted-foreground md:hidden">Handpicked offers for you</p>
                  </div>
                </div>
                <Link to="/products" className="flex items-center gap-0.5 text-[11px] font-bold text-primary active:opacity-70">
                  View All <ChevronRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </FadeIn>

            {productsLoading ? (
              <ProductSkeletonRow count={6} />
            ) : featured.length > 0 ? (
              <>
                <div className="md:hidden flex overflow-x-auto gap-2.5 scrollbar-hide pb-2 -mx-3 px-3 snap-x snap-mandatory">
                  {featured.slice(0, 8).map((product) => (
                    <div key={product.id} className="shrink-0 w-[145px] snap-start">
                      <MobileProductCard product={product} />
                    </div>
                  ))}
                </div>
                <div className="hidden md:grid gap-4 grid-cols-2 lg:grid-cols-4">
                  {featured.slice(0, 8).map((product, i) => (
                    <FadeIn key={product.id} delay={i * 0.04}>
                      <ProductCard product={product} />
                    </FadeIn>
                  ))}
                </div>
              </>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-6">No featured deals right now</p>
            )}
          </div>
        </section>

        {/* ═══ ALL PRODUCTS GRID ═══ */}
        <section className="py-4 md:py-10 bg-muted/15 border-t border-border/10">
          <div className="container max-w-7xl px-3 md:px-4">
            <FadeIn>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="h-5 w-1 rounded-full bg-accent md:h-6 md:w-1.5" />
                  <h2 className="font-heading text-[15px] md:text-2xl font-black text-foreground">Shop All Products</h2>
                </div>
                <Link to="/products" className="flex items-center gap-0.5 text-[11px] font-bold text-primary active:opacity-70">
                  View All <ChevronRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </FadeIn>

            {productsLoading ? (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 md:gap-4">
                {Array.from({ length: 8 }).map((_, i) => <ProductSkeleton key={i} />)}
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-2 md:hidden">
                  {allProducts.map((product, i) => (
                    <FadeIn key={product.id} delay={i * 0.03}>
                      <MobileProductCard product={product} />
                    </FadeIn>
                  ))}
                </div>
                <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {allProducts.map((product, i) => (
                    <FadeIn key={product.id} delay={i * 0.03}>
                      <ProductCard product={product} />
                    </FadeIn>
                  ))}
                </div>
              </>
            )}
          </div>
        </section>

        {/* ═══ SELL & BULK CTA — CMS driven ═══ */}
        <section className="py-4 md:py-10 bg-background">
          <div className="container max-w-7xl px-3 md:px-4">
            <h2 className="sr-only">Sell Your Device or Buy in Bulk</h2>
            <div className="grid md:grid-cols-2 gap-2.5 md:gap-5">
              {ctaItems.map((cta: any, i: number) => (
                <Link
                  key={i}
                  to={cta.link}
                  className={`group relative rounded-xl md:rounded-2xl p-4 md:p-8 flex flex-col overflow-hidden active:scale-[0.98] transition-transform ${
                    cta.style === "filled"
                      ? "bg-secondary text-secondary-foreground"
                      : "bg-card border border-border text-foreground"
                  }`}
                >
                  <div className="absolute -bottom-8 -right-8 w-32 h-32 rounded-full bg-primary/20 blur-3xl opacity-60" />
                  <span className="relative text-[9px] font-bold uppercase tracking-widest text-primary/80 mb-1">
                    {cta.badge_emoji} {cta.badge_text}
                  </span>
                  <h3 className="relative font-heading text-[15px] md:text-2xl font-black mb-1">{cta.heading}</h3>
                  <p className="relative text-[11px] md:text-sm opacity-70 mb-3 max-w-xs">{cta.description}</p>
                  <span className="relative text-[11px] font-bold text-primary flex items-center gap-1 mt-auto">
                    {cta.cta_text} <ArrowRight className="h-3 w-3 group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* ═══ HAPPY CUSTOMERS ═══ */}
        <Suspense fallback={<div className="py-8" />}>
          <HappyCustomers />
        </Suspense>

        {/* ═══ LATEST FROM BLOG ═══ */}
        <Suspense fallback={<div className="py-8" />}>
          <LatestBlogs />
        </Suspense>

        {/* ═══ FAQ — CMS driven ═══ */}
        <section className="py-5 md:py-12 bg-card border-t border-border/20">
          <div className="container max-w-5xl px-3 md:px-4">
            <FadeIn>
              <h2 className="font-heading text-[15px] md:text-2xl font-black text-foreground text-center mb-4">
                Frequently Asked Questions
              </h2>
            </FadeIn>
            <div className="grid md:grid-cols-2 gap-x-10 gap-y-0">
              {faqs.map((f, i) => (
                <FadeIn key={i} delay={i * 0.03}>
                  <FAQItem q={f.q} a={f.a} />
                </FadeIn>
              ))}
            </div>
            <FadeIn delay={0.15}>
              <div className="mt-5 max-w-md mx-auto rounded-xl bg-muted/40 border border-border/50 p-4 flex flex-col sm:flex-row items-center justify-between gap-3 text-center sm:text-left">
                <div>
                  <h3 className="font-heading text-[13px] font-black text-foreground">Still need help?</h3>
                  <p className="text-[10px] text-muted-foreground">Chat with our experts.</p>
                </div>
                <Link to="/contact-us">
                  <Button size="sm" className="rounded-full h-9 px-5 font-bold text-[12px]">
                    Contact Us <ArrowRight className="ml-1 h-3 w-3" />
                  </Button>
                </Link>
              </div>
            </FadeIn>
          </div>
        </section>
      </div>
    </PageTransition>
  );
};

export default Index;
